import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import Head from 'src/layout/head/Head';
import { getPlaybookById, getStepTypeBadgeColor, getScheduleLabel, getPlaybookDescription, getPlaybookSchedule, getStepsDetail } from './data';
import {
  ArrowLeftIcon,
  PencilIcon,
  PlayIcon,
  PauseIcon,
  ClockIcon,
  CommandLineIcon,
  XMarkIcon,
  DocumentDuplicateIcon,
  SparklesIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  StopIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  ArrowsPointingOutIcon,
  ViewfinderCircleIcon,
  EyeIcon,
  CubeIcon,
  ArrowRightIcon,
  HashtagIcon,
  KeyIcon,
  PlusIcon,
  TrashIcon,
  CodeBracketIcon
} from '@heroicons/react/24/outline';
import moment from 'moment';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Handle,
  Position,
  useReactFlow
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';

// Integration and node type icons mapping
const IntegrationIcon = ({ integration, nodeType, className = "w-4 h-4" }) => {
  // Integration-specific icons
  const integrationIcons = {
    felloh: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" />
      </svg>
    ),
    pipedrive: (
      <div className={`${className} rounded bg-green-600 flex items-center justify-center text-white font-bold text-xs`}>P</div>
    ),
    xero: (
      <div className={`${className} rounded bg-cyan-500 flex items-center justify-center text-white font-bold text-xs`}>X</div>
    ),
    sage: (
      <div className={`${className} rounded bg-green-500 flex items-center justify-center text-white font-bold text-xs`}>S</div>
    ),
    email: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    slack: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M6 15a2 2 0 01-2 2 2 2 0 01-2-2 2 2 0 012-2h2v2zm1 0a2 2 0 012-2 2 2 0 012 2v5a2 2 0 01-2 2 2 2 0 01-2-2v-5zm2-8a2 2 0 01-2-2 2 2 0 012-2 2 2 0 012 2v2H9zm0 1a2 2 0 012 2 2 2 0 01-2 2H4a2 2 0 01-2-2 2 2 0 012-2h5zm8 2a2 2 0 012-2 2 2 0 012 2 2 2 0 01-2 2h-2v-2zm-1 0a2 2 0 01-2 2 2 2 0 01-2-2V5a2 2 0 012-2 2 2 0 012 2v5zm-2 8a2 2 0 012 2 2 2 0 01-2 2 2 2 0 01-2-2v-2h2zm0-1a2 2 0 01-2-2 2 2 0 012-2h5a2 2 0 012 2 2 2 0 01-2 2h-5z" />
      </svg>
    )
  };

  if (integration && integrationIcons[integration]) {
    return integrationIcons[integration];
  }

  // Node type icons (for nodes without specific integration)
  const type = nodeType || '';

  // Webhook/trigger icon
  if (type.includes('webhook') || type.includes('trigger')) {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    );
  }

  // Code icon
  if (type.includes('code')) {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M16 18l6-6-6-6M8 6l-6 6 6 6" />
      </svg>
    );
  }

  // If/Filter icon
  if (type.includes('if') || type.includes('filter')) {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
      </svg>
    );
  }

  // HTTP Request icon
  if (type.includes('httpRequest')) {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    );
  }

  // Set/transform icon
  if (type.includes('set')) {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <line x1="9" y1="3" x2="9" y2="21" />
      </svg>
    );
  }

  // Split icon
  if (type.includes('split')) {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5" />
      </svg>
    );
  }

  // Default icon (cube/step)
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
    </svg>
  );
};

// Simple connector line for legacy steps list
const Connector = ({ height = 'h-6' }) => (
  <div className={`w-0.5 ${height} bg-violet-500 dark:bg-violet-400`} />
);

// Get icon color based on node type or integration
const getNodeIconStyle = (node) => {
  const integration = node.integration;
  const type = node.type || '';

  if (integration === 'felloh') return { bg: 'bg-violet-500', text: 'text-white' };
  if (integration === 'pipedrive') return { bg: 'bg-emerald-500', text: 'text-white' };
  if (integration === 'xero') return { bg: 'bg-cyan-400', text: 'text-white' };
  if (integration === 'email') return { bg: 'bg-rose-400', text: 'text-white' };
  if (integration === 'slack') return { bg: 'bg-violet-500', text: 'text-white' };
  if (integration === 'sage') return { bg: 'bg-green-600', text: 'text-white' };

  if (type.includes('webhook') || type.includes('trigger')) return { bg: 'bg-teal-500', text: 'text-white' };
  if (type.includes('if') || type.includes('filter')) return { bg: 'bg-amber-500', text: 'text-white' };
  if (type.includes('switch')) return { bg: 'bg-rose-500', text: 'text-white' };
  if (type.includes('code')) return { bg: 'bg-orange-500', text: 'text-white' };
  if (type.includes('httpRequest')) return { bg: 'bg-blue-500', text: 'text-white' };
  if (type.includes('set')) return { bg: 'bg-indigo-400', text: 'text-white' };
  if (type.includes('split')) return { bg: 'bg-pink-500', text: 'text-white' };

  return { bg: 'bg-gray-500', text: 'text-white' };
};

// Get short subtitle for node
const getNodeSubtitle = (node) => {
  const type = node.type || '';
  const params = node.parameters || {};

  if (params.operation) return params.operation;
  if (params.path) return params.path;
  if (type.includes('webhook')) return 'webhook';
  if (type.includes('trigger')) return 'trigger';
  if (type.includes('filter')) return 'filter';
  if (type.includes('if')) return 'condition';
  if (type.includes('switch')) return 'router';
  if (type.includes('code')) return 'function';
  if (type.includes('httpRequest')) return 'HTTP request';
  if (type.includes('set')) return 'set values';
  if (type.includes('split')) return 'split data';

  return null;
};

// Custom React Flow Node for workflow steps - n8n style
const StepNode = ({ data, selected }) => {
  const { node, onClick } = data;
  const iconStyle = getNodeIconStyle(node);
  const subtitle = getNodeSubtitle(node);

  return (
    <div className="flex flex-col items-center">
      {/* Node card */}
      <div
        onClick={() => onClick && onClick(node)}
        className={`relative bg-gray-800 rounded-lg w-[56px] h-[56px] flex items-center justify-center shadow-lg transition-all hover:shadow-xl cursor-pointer ${selected
          ? 'ring-2 ring-violet-500 ring-offset-2 ring-offset-violet-50 dark:ring-offset-violet-950'
          : ''
          }`}
      >
        <Handle
          type="target"
          position={Position.Left}
          className="!bg-gray-600 !w-3 !h-3 !border-2 !border-gray-400 !-left-1.5"
        />

        {/* Icon container */}
        <div className={`w-8 h-8 rounded-md ${iconStyle.bg} flex items-center justify-center`}>
          <IntegrationIcon integration={node.integration} nodeType={node.type} className={`w-5 h-5 ${iconStyle.text}`} />
        </div>

        <Handle
          type="source"
          position={Position.Right}
          className="!bg-gray-600 !w-3 !h-3 !border-2 !border-gray-400 !-right-1.5"
        />
      </div>

      {/* Label below node */}
      <div className="mt-2 text-center">
        <div className="text-xs font-medium text-gray-700 dark:text-gray-200 whitespace-nowrap">
          {node.name}
        </div>
        {subtitle && (
          <div className="text-[10px] text-gray-500 dark:text-gray-400">
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );
};

// Custom React Flow Node for branch labels (Success, Error, etc.) - n8n style
const BranchLabelNode = ({ data }) => {
  const { label, type } = data;
  const styles = {
    success: 'text-emerald-400',
    error: 'text-rose-400',
    path: 'text-gray-400'
  };

  const handleClasses = {
    success: '!bg-emerald-500',
    error: '!bg-rose-500',
    path: '!bg-gray-500'
  };

  return (
    <div className="flex items-center">
      <Handle
        type="target"
        position={Position.Left}
        className={`!w-2.5 !h-2.5 !border-0 ${handleClasses[type] || handleClasses.path} !-left-1`}
      />
      <span className={`text-[10px] font-medium px-2 py-0.5 whitespace-nowrap ${styles[type] || styles.path}`}>
        {label}
      </span>
      <Handle
        type="source"
        position={Position.Right}
        className={`!w-2.5 !h-2.5 !border-0 ${handleClasses[type] || handleClasses.path} !-right-1`}
      />
    </div>
  );
};

// Node types for React Flow
const nodeTypes = {
  stepNode: StepNode,
  branchLabel: BranchLabelNode
};

// Determine integration from node type
const getIntegration = (type) => {
  if (!type) return null;
  if (type.includes('felloh')) return 'felloh';
  if (type.includes('pipedrive')) return 'pipedrive';
  if (type.includes('xero')) return 'xero';
  if (type.includes('slack')) return 'slack';
  if (type.includes('email') || type.includes('Email')) return 'email';
  if (type.includes('sage') || type.includes('Sage')) return 'sage';
  return null;
};

// Node dimensions for dagre layout
const NODE_WIDTH = 160;
const NODE_HEIGHT = 100;

// Create dagre graph and apply layout for horizontal flow
const getLayoutedElements = (nodes, edges) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: 'LR', nodesep: 80, ranksep: 120 });

  // Add nodes to dagre
  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  });

  // Add edges to dagre
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Calculate layout
  dagre.layout(dagreGraph);

  // Apply positions to nodes
  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - NODE_WIDTH / 2,
        y: nodeWithPosition.y - NODE_HEIGHT / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

// Convert n8n nodes and connections directly to React Flow format (supports convergence)
const convertN8nToFlow = (n8nNodes, connections, onNodeClick, selectedNodeId) => {
  if (!n8nNodes || n8nNodes.length === 0) return { nodes: [], edges: [] };

  const nodes = [];
  const edges = [];
  const nodeByName = {};

  // Build node lookup by name
  n8nNodes.forEach(node => {
    nodeByName[node.name] = node;
  });

  // Create React Flow nodes for each n8n node
  n8nNodes.forEach(node => {
    // Skip disabled nodes
    if (node.disabled) return;

    const flowNode = {
      id: node.id,
      type: 'stepNode',
      position: { x: 0, y: 0 },
      data: {
        node: {
          ...node,
          integration: getIntegration(node.type)
        },
        onClick: onNodeClick
      },
      selected: node.id === selectedNodeId
    };
    nodes.push(flowNode);
  });

  // Create edges from connections
  Object.entries(connections || {}).forEach(([sourceName, connectionData]) => {
    const sourceNode = nodeByName[sourceName];
    if (!sourceNode || sourceNode.disabled) return;

    if (connectionData?.main) {
      connectionData.main.forEach((outputs, outputIndex) => {
        if (!outputs || !Array.isArray(outputs)) return;

        outputs.forEach(conn => {
          const targetNode = nodeByName[conn.node];
          if (!targetNode || targetNode.disabled) return;

          // Determine edge color based on output index for branching nodes
          const isIfNode = sourceNode.type?.includes('if');
          let edgeColor = '#6b7280'; // default gray

          if (isIfNode) {
            // For if nodes: output 0 = true (green), output 1 = false (gray)
            edgeColor = outputIndex === 0 ? '#10b981' : '#6b7280';
          }

          // Create unique edge id
          const edgeId = `e-${sourceNode.id}-${outputIndex}-${targetNode.id}`;

          // Check if edge already exists (avoid duplicates)
          if (!edges.find(e => e.id === edgeId)) {
            edges.push({
              id: edgeId,
              source: sourceNode.id,
              target: targetNode.id,
              type: 'smoothstep',
              style: { stroke: edgeColor, strokeWidth: 2 },
              label: isIfNode ? (outputIndex === 0 ? 'true' : 'false') : undefined,
              labelStyle: { fill: '#6b7280', fontSize: 10, fontWeight: 500 },
              labelBgStyle: { fill: '#ffffff', fillOpacity: 0.9 },
              labelBgPadding: [4, 2],
              labelBgBorderRadius: 2
            });
          }
        });
      });
    }
  });

  // Apply dagre layout
  return getLayoutedElements(nodes, edges);
};

// Navigation controls for the tree view
const TreeViewControls = () => {
  const { zoomIn, zoomOut, fitView } = useReactFlow();

  const handleZoomIn = () => {
    zoomIn();
  };

  const handleZoomOut = () => {
    zoomOut();
  };

  const handleFitView = () => {
    fitView({ padding: 0.2 });
  };

  const handleResetZoom = () => {
    fitView({ padding: 0.2, maxZoom: 1 });
  };

  return (
    <div className="absolute bottom-4 right-4 z-10 flex items-center gap-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-violet-200 dark:border-gray-600 p-1 clearfix h-auto">
      <button
        onClick={handleZoomOut}
        className="p-2 hover:bg-violet-100 dark:hover:bg-gray-700 rounded-md transition-colors text-gray-500 dark:text-gray-400 hover:text-violet-700 dark:hover:text-white"
        title="Zoom out"
      >
        <MagnifyingGlassMinusIcon className="w-4 h-4" />
      </button>
      <div className="w-px h-5 bg-violet-200 dark:bg-gray-600" />
      <button
        onClick={handleZoomIn}
        className="p-2 hover:bg-violet-100 dark:hover:bg-gray-700 rounded-md transition-colors text-gray-500 dark:text-gray-400 hover:text-violet-700 dark:hover:text-white"
        title="Zoom in"
      >
        <MagnifyingGlassPlusIcon className="w-4 h-4" />
      </button>
      <div className="w-px h-5 bg-violet-200 dark:bg-gray-600" />
      <button
        onClick={handleFitView}
        className="p-2 hover:bg-violet-100 dark:hover:bg-gray-700 rounded-md transition-colors text-gray-500 dark:text-gray-400 hover:text-violet-700 dark:hover:text-white"
        title="Fit to view"
      >
        <ArrowsPointingOutIcon className="w-4 h-4" />
      </button>
      <div className="w-px h-5 bg-violet-200 dark:bg-gray-600" />
      <button
        onClick={handleResetZoom}
        className="p-2 hover:bg-violet-100 dark:hover:bg-gray-700 rounded-md transition-colors text-gray-500 dark:text-gray-400 hover:text-violet-700 dark:hover:text-white"
        title="Reset zoom"
      >
        <ViewfinderCircleIcon className="w-4 h-4" />
      </button>
    </div>
  );
};

// Inner component that uses React Flow hooks
const WorkflowTreeInner = ({ nodes, edges, onNodeClick }) => {
  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodeClick={onNodeClick}
      nodeTypes={nodeTypes}
      defaultEdgeOptions={{
        type: 'smoothstep',
        style: { strokeWidth: 2, stroke: '#6b7280' }
      }}
      fitView
      fitViewOptions={{ padding: 0.3, includeHiddenNodes: false }}
      minZoom={0.3}
      maxZoom={2}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={false}
      panOnDrag={true}
      zoomOnScroll={true}
      proOptions={{ hideAttribution: true }}
    >
      <Background color="#c4b5fd" gap={20} size={1} variant="dots" />
      <TreeViewControls />
    </ReactFlow>
  );
};

// Main workflow tree component using React Flow - n8n style with graph support
const WorkflowTree = ({ n8nNodes, connections, onNodeSelect, selectedNodeId }) => {
  const { nodes, edges } = useMemo(
    () => n8nNodes ? convertN8nToFlow(n8nNodes, connections, onNodeSelect, selectedNodeId) : { nodes: [], edges: [] },
    [n8nNodes, connections, onNodeSelect, selectedNodeId]
  );

  const onNodeClick = useCallback((_event, node) => {
    if (node.data.onClick && node.data.node) {
      node.data.onClick(node.data.node);
    }
  }, []);

  if (!n8nNodes || n8nNodes.length === 0) return null;

  return (
    <div className="h-[500px] w-full rounded-xl overflow-hidden border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-950">
      <style>{`
        .react-flow__edges {
          width: 100% !important;
          height: 100% !important;
        }
        .react-flow__background {
          background-color: #f5f3ff !important;
        }
        .dark .react-flow__background {
          background-color: #2e1065 !important;
        }
        .react-flow__edge-text {
          font-size: 10px;
        }
      `}</style>
      <ReactFlowProvider>
        <WorkflowTreeInner nodes={nodes} edges={edges} onNodeClick={onNodeClick} />
      </ReactFlowProvider>
    </div>
  );
};

// Step detail panel shown when a tree node is clicked
const StepDetailPanel = ({ node, onClose, onNavigateToNode }) => {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [jsonValue, setJsonValue] = useState('');
  const [jsonError, setJsonError] = useState(null);

  if (!node) return null;

  const hasIntegration = node.integration;
  const configEntries = node.config
    ? Object.entries(node.config).filter(([key]) => key !== 'integration')
    : [];

  // Count children and branches
  const childrenCount = node.children?.length || 0;
  const branchesCount = node.branches?.length || 0;
  const totalConnections = childrenCount + (branchesCount > 0 ? node.branches.reduce((sum, b) => sum + (b.children?.length || 0), 0) : 0);

  // Get a friendly type name
  const getTypeName = (type) => {
    if (!type) return 'Step';
    if (type.includes('trigger')) return 'Trigger';
    if (type.includes('webhook')) return 'Webhook';
    if (type.includes('if')) return 'Condition';
    if (type.includes('switch')) return 'Switch';
    if (type.includes('code')) return 'Code';
    if (type.includes('http')) return 'HTTP Request';
    return type.split('.').pop() || 'Step';
  };

  // Prepare node data for JSON editor (exclude children/branches to avoid circular refs)
  const getEditableNodeData = () => {
    const { children, branches, ...editableData } = node;
    return editableData;
  };

  const handleOpenEditModal = () => {
    const editableData = getEditableNodeData();
    setJsonValue(JSON.stringify(editableData, null, 2));
    setJsonError(null);
    setEditModalOpen(true);
  };

  const handleJsonChange = (e) => {
    const value = e.target.value;
    setJsonValue(value);
    try {
      JSON.parse(value);
      setJsonError(null);
    } catch (err) {
      setJsonError(err.message);
    }
  };

  const handleSaveJson = () => {
    try {
      JSON.parse(jsonValue);
      // In a real implementation, this would update the node via an API call
      // For now, just close the modal
      setEditModalOpen(false);
    } catch (err) {
      setJsonError(err.message);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-5 min-w-[360px] max-w-[420px]">
      {/* JSON Edit Modal */}
      <Modal isOpen={editModalOpen} toggle={() => setEditModalOpen(false)} size="lg">
        <ModalHeader
          toggle={() => setEditModalOpen(false)}
          className="bg-violet-800 text-xl text-white pt-4 pb-3"
          close={
            <button className="-mt-1 text-white" onClick={() => setEditModalOpen(false)}>
              <XMarkIcon className="h-6 w-6" />
            </button>
          }
        >
          <span className="flex items-center gap-2">
            <CodeBracketIcon className="h-5 w-5" />
            Edit Step: {node.name}
          </span>
        </ModalHeader>
        <ModalBody className="p-0">
          <div className="relative">
            <textarea
              value={jsonValue}
              onChange={handleJsonChange}
              className={`w-full h-[500px] p-4 font-mono text-sm bg-gray-900 text-green-400 focus:outline-none resize-none ${
                jsonError ? 'border-b-4 border-red-500' : ''
              }`}
              spellCheck={false}
            />
            {jsonError && (
              <div className="absolute bottom-0 left-0 right-0 bg-red-500 text-white text-xs px-4 py-2">
                <span className="font-semibold">JSON Error:</span> {jsonError}
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter className="bg-light">
          <button
            type="button"
            onClick={() => setEditModalOpen(false)}
            className="btn btn-outline-secondary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSaveJson}
            disabled={!!jsonError}
            className="btn bg-violet-700 hover:bg-violet-800 text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Save Changes
          </button>
        </ModalFooter>
      </Modal>

      {/* Header */}
      <div className="flex items-start justify-between gap-3 clearfix h-auto">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {hasIntegration && (
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium ${getStepTypeBadgeColor(null, node.integration)}`}>
                <IntegrationIcon integration={node.integration} className="w-4 h-4" />
                {node.integration.charAt(0).toUpperCase() + node.integration.slice(1)}
              </span>
            )}
            {node.type && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                <CubeIcon className="w-3.5 h-3.5" />
                {getTypeName(node.type)}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={handleOpenEditModal}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
            title="Edit step JSON"
          >
            <CodeBracketIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>
      </div>

      {/* Title */}
      <h3 className={`text-lg font-semibold text-gray-900 dark:text-white mt-3 ${!node.description ? 'mb-4' : ''}`}>
        {node.name}
      </h3>

      {/* Description */}
      {node.description && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {node.description}
        </p>
      )}

      {/* Step Info Grid */}
      <div className="grid grid-cols-2 gap-3 clearfix h-auto">
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 pb-4 clearfix h-auto">
          <div className="flex items-center gap-1.5 mb-2">
            <HashtagIcon className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Step ID</span>
          </div>
          <p className="text-sm font-mono text-gray-900 dark:text-white truncate" title={node.id}>
            {node.id}
          </p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 pb-4 clearfix h-auto">
          <div className="flex items-center gap-1.5 mb-2">
            <ArrowRightIcon className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Connections</span>
          </div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {totalConnections > 0 ? `${totalConnections} outgoing` : 'End of flow'}
          </p>
        </div>
      </div>

      {/* Node Type */}
      {node.type && (
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 mb-4">
          <code className="text-xs text-violet-600 dark:text-violet-400 font-mono break-all">
            {node.type.replace('n8n-nodes-','')}
          </code>
        </div>
      )}

      {/* Configuration */}
      {configEntries.length > 0 && (
        <div className="mb-4">
          <h4 className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
            Configuration
          </h4>
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 font-mono text-xs space-y-2 max-h-48 overflow-y-auto">
            {configEntries.map(([key, value]) => (
              <div key={key} className="break-all">
                <span className="text-gray-500 dark:text-gray-400">{key}:</span>{' '}
                <span className="text-green-600 dark:text-green-400">
                  {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Branches */}
      {node.branches && node.branches.length > 0 && (
        <div className="mb-4">
          <h4 className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
            Branches ({node.branches.length})
          </h4>
          <div className="space-y-2">
            {node.branches.map((branch) => (
              <div
                key={branch.id}
                className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm ${branch.type === 'success'
                  ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                  : branch.type === 'error'
                    ? 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300'
                    : 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300'
                  }`}
              >
                <span className="font-medium">{branch.label}</span>
                <span className="text-xs opacity-75">
                  {branch.children?.length || 0} step{(branch.children?.length || 0) !== 1 ? 's' : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Children */}
      {childrenCount > 0 && !branchesCount && (
        <div>
          <h4 className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
            Next Steps ({childrenCount})
          </h4>
          <div className="space-y-1.5">
            {node.children.slice(0, 3).map((child) => (
              <button
                key={child.id}
                onClick={() => onNavigateToNode && onNavigateToNode(child)}
                className="w-full flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-sm transition-colors text-left clearfix h-auto"
              >
                <ArrowRightIcon className="w-4 h-4 text-violet-500 dark:text-violet-400 flex-shrink-0" />
                <span className="text-gray-900 dark:text-white truncate flex-1">{child.name}</span>
                {child.integration && (
                  <span className="flex-shrink-0">
                    <IntegrationIcon integration={child.integration} className="w-4 h-4" />
                  </span>
                )}
              </button>
            ))}
            {childrenCount > 3 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-1">
                +{childrenCount - 3} more step{childrenCount - 3 !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Legacy flat steps renderer for playbooks without tree structure
const LegacyStepsList = ({ steps }) => {
  return (
    <div className="space-y-4">
      {steps.map((step, index) => (
        <div key={step.id} className="flex flex-col items-center">
          {index > 0 && <Connector height="h-6" />}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-4 max-w-md w-full">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-100 dark:bg-violet-900 flex items-center justify-center">
                <span className="text-xs font-semibold text-violet-700 dark:text-violet-300">{index + 1}</span>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{step.name}</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStepTypeBadgeColor(step.type)}`}>
                    {step.type}
                  </span>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400">{step.description}</p>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Step type icon for the steps view
const StepTypeIcon = ({ type }) => {
  const icons = {
    webhook: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </svg>
    ),
    condition: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5" />
      </svg>
    ),
    action: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
      </svg>
    ),
    delay: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
      </svg>
    ),
    schedule: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    ),
    loop: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 1l4 4-4 4" />
        <path d="M3 11V9a4 4 0 0 1 4-4h14" />
        <path d="M7 23l-4-4 4-4" />
        <path d="M21 13v2a4 4 0 0 1-4 4H3" />
      </svg>
    )
  };
  return icons[type] || icons.action;
};

// Format config value for display
const formatConfigValue = (value) => {
  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value);
  }
  return String(value);
};

// Steps view - flat list format
const StepsView = ({ n8nNodes, connections }) => {
  if (!n8nNodes || n8nNodes.length === 0) return null;

  // Filter out disabled nodes and sort by position if available
  const activeNodes = n8nNodes.filter(node => !node.disabled);

  return (
    <div>
      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-6">
        Steps
      </div>
      <div className="space-y-3">
        {activeNodes.map((node, index) => {
          const integration = getIntegration(node.type);
          const nodeType = node.type || '';

          // Determine step type for badge
          let stepType = 'action';
          if (nodeType.includes('webhook') || nodeType.includes('trigger')) stepType = 'trigger';
          else if (nodeType.includes('if') || nodeType.includes('filter')) stepType = 'condition';
          else if (nodeType.includes('switch')) stepType = 'router';
          else if (nodeType.includes('code')) stepType = 'code';
          else if (nodeType.includes('httpRequest')) stepType = 'http';

          const isConditional = nodeType.includes('if') || nodeType.includes('switch');

          return (
            <div key={node.id} className={`rounded-lg p-4 border ${isConditional ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30' : 'border-gray-200 dark:border-gray-700'}`}>
              <div className="flex items-start gap-4">
                {/* Step number */}
                <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${isConditional ? 'bg-amber-100 dark:bg-amber-900/50' : 'bg-gray-100 dark:bg-gray-700'}`}>
                  <span className={`text-sm font-medium ${isConditional ? 'text-amber-700 dark:text-amber-300' : 'text-gray-600 dark:text-gray-300'}`}>{index + 1}</span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      {node.name}
                    </span>
                    {/* Type badge */}
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${isConditional ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300' : 'border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'}`}>
                      <StepTypeIcon type={stepType} />
                      {stepType}
                    </span>
                    {/* Integration badge */}
                    {integration && (
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStepTypeBadgeColor(null, integration)}`}>
                        <IntegrationIcon integration={integration} nodeType={nodeType} className="w-3.5 h-3.5" />
                        {integration}
                      </span>
                    )}
                  </div>

                  {/* Description based on node notes */}
                  {node.notes && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {node.notes.substring(0, 100)}{node.notes.length > 100 ? '...' : ''}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Secrets View component for managing playbook secrets
const SecretsView = ({ secrets, onAddSecret, onEditSecret, onDeleteSecret }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSecret, setEditingSecret] = useState(null);
  const [formData, setFormData] = useState({ key: '', value: '', description: '' });

  const handleOpenAddModal = () => {
    setFormData({ key: '', value: '', description: '' });
    setEditingSecret(null);
    setShowAddModal(true);
  };

  const handleOpenEditModal = (secret) => {
    setFormData({ key: secret.key, value: '', description: secret.description || '' });
    setEditingSecret(secret);
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingSecret(null);
    setFormData({ key: '', value: '', description: '' });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingSecret) {
      onEditSecret({ ...editingSecret, ...formData, updatedAt: new Date().toISOString() });
    } else {
      onAddSecret({
        id: `secret-${Date.now()}`,
        ...formData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
    handleCloseModal();
  };

  const maskValue = (value) => {
    if (!value) return '••••••••';
    return '•'.repeat(Math.min(value.length, 24));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Secrets & Credentials</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage API keys, tokens, and other sensitive values used by this playbook.
          </p>
        </div>
        <button
          onClick={handleOpenAddModal}
          className="inline-flex items-center gap-2 px-4 py-2 bg-violet-700 hover:bg-violet-800 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          Add Secret
        </button>
      </div>

      {secrets.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-900 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
          <KeyIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No secrets configured</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Add secrets to securely store API keys and credentials for your integrations.
          </p>
          <button
            onClick={handleOpenAddModal}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-violet-700 hover:bg-violet-800 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Add your first secret
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {secrets.map((secret) => (
            <div
              key={secret.id}
              className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-violet-100 dark:bg-violet-900/50 rounded-lg">
                    <KeyIcon className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white font-mono">
                      {secret.key}
                    </h4>
                    {secret.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {secret.description}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleOpenEditModal(secret)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors text-gray-500 dark:text-gray-400"
                    title="Edit secret"
                  >
                    <PencilIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => onDeleteSecret(secret.id)}
                    className="p-2 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-md transition-colors text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                    title="Delete secret"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3 ml-12 clearfix h-auto">
                <div className="flex-1 bg-gray-100 dark:bg-gray-900 rounded-md px-3 py-2 font-mono text-sm">
                  <span className="text-gray-600 dark:text-gray-400">
                    {maskValue(secret.value)}
                  </span>
                </div>
              </div>
              <div className="mt-3 ml-12 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-500">
                <span>Created {moment(secret.createdAt).format('MMM D, YYYY')}</span>
                {secret.updatedAt !== secret.createdAt && (
                  <span>Updated {moment(secret.updatedAt).fromNow()}</span>
                )}
                {secret.usedIn && (
                  <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-full">
                    Used in {secret.usedIn.length} step{secret.usedIn.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Secret Modal */}
      <Modal isOpen={showAddModal} toggle={handleCloseModal} size="md">
        <ModalHeader
          toggle={handleCloseModal}
          className="bg-violet-800 text-xl text-white pt-4 pb-3"
          close={
            <button className="-mt-1 text-white" onClick={handleCloseModal}>
              <XMarkIcon className="h-6 w-6" />
            </button>
          }
        >
          <span className="flex items-center gap-2">
            <KeyIcon className="h-5 w-5" />
            {editingSecret ? 'Edit Secret' : 'Add Secret'}
          </span>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody className="p-6">
            <div className="space-y-4">
              <div>
                <label htmlFor="secret-key" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Secret Name
                </label>
                <input
                  type="text"
                  id="secret-key"
                  value={formData.key}
                  onChange={(e) => setFormData(prev => ({ ...prev, key: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') }))}
                  placeholder="e.g., XERO_API_KEY"
                  className="block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 font-mono"
                  required
                  disabled={!!editingSecret}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Use SCREAMING_SNAKE_CASE. Only letters, numbers, and underscores allowed.
                </p>
              </div>

              <div>
                <label htmlFor="secret-value" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Secret Value
                </label>
                <input
                  type="password"
                  id="secret-value"
                  value={formData.value}
                  onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value }))}
                  placeholder={editingSecret ? 'Enter new value to update' : 'Enter secret value'}
                  className="block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 font-mono"
                  required={!editingSecret}
                />
                {editingSecret && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Leave empty to keep the existing value.
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="secret-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  id="secret-description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="e.g., API key for Xero accounting integration"
                  className="block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                />
              </div>

              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 clearfix h-auto">
                <div className="flex items-start gap-3 clearfix h-auto">
                  <ExclamationCircleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800 dark:text-amber-200 clearfix h-auto">
                    <p className="font-medium">Security Notice</p>
                    <p className="mt-1 text-amber-700 dark:text-amber-300">
                      Secrets are encrypted at rest and only decrypted during playbook execution. Never share secret values in logs or error messages.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </ModalBody>

          <ModalFooter className="bg-light">
            <button
              type="button"
              onClick={handleCloseModal}
              className="btn btn-outline-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn bg-violet-700 hover:bg-violet-800 text-white"
            >
              {editingSecret ? 'Update Secret' : 'Add Secret'}
            </button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
};

// Frequency options for edit modal
const FREQUENCY_OPTIONS = [
  { value: 'on_event', label: 'On event trigger' },
  { value: 'every_5_minutes', label: 'Every 5 minutes' },
  { value: 'every_15_minutes', label: 'Every 15 minutes' },
  { value: 'every_hour', label: 'Every hour' },
  { value: 'every_6_hours', label: 'Every 6 hours' },
  { value: 'every_day', label: 'Every day' },
  { value: 'every_week', label: 'Every week' },
  { value: 'every_month', label: 'Every month' },
];

// Format duration in human readable format
const formatDuration = (ms) => {
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
};

// Test Run Status Box component
const TestRunStatusBox = ({ testRun, onClose, onStop, stepsCount }) => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const intervalRef = useRef(null);

  const status = testRun?.status;
  const startTime = testRun?.startTime;
  const currentStep = testRun?.currentStep;
  const completedSteps = testRun?.completedSteps || 0;
  const error = testRun?.error;

  useEffect(() => {
    if (!testRun) return;

    if (status === 'running') {
      intervalRef.current = setInterval(() => {
        setElapsedTime(Date.now() - startTime);
      }, 100);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      setElapsedTime(testRun.duration || Date.now() - startTime);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [testRun, status, startTime]);

  if (!testRun) return null;

  const progressPercent = stepsCount > 0 ? Math.round((completedSteps / stepsCount) * 100) : 0;

  const statusConfig = {
    running: {
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-200 dark:border-blue-800',
      icon: <ArrowPathIcon className="w-5 h-5 text-blue-500 animate-spin" />,
      text: 'text-blue-700 dark:text-blue-300',
      label: 'Running'
    },
    success: {
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-200 dark:border-green-800',
      icon: <CheckCircleIcon className="w-5 h-5 text-green-500" />,
      text: 'text-green-700 dark:text-green-300',
      label: 'Completed'
    },
    error: {
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-200 dark:border-red-800',
      icon: <ExclamationCircleIcon className="w-5 h-5 text-red-500" />,
      text: 'text-red-700 dark:text-red-300',
      label: 'Failed'
    },
    stopped: {
      bg: 'bg-yellow-50 dark:bg-yellow-900/20',
      border: 'border-yellow-200 dark:border-yellow-800',
      icon: <StopIcon className="w-5 h-5 text-yellow-500" />,
      text: 'text-yellow-700 dark:text-yellow-300',
      label: 'Stopped'
    }
  };

  const config = statusConfig[status] || statusConfig.running;

  return (
    <div className={`mb-6 rounded-lg border ${config.border} ${config.bg} p-4 overflow-hidden`}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="flex-shrink-0">
            {config.icon}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className={`text-sm font-semibold ${config.text}`}>
                Test Run {config.label}
              </h3>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Started {moment(startTime).format('HH:mm:ss')}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">•</span>
              <span className="text-xs text-gray-600 dark:text-gray-400 font-mono">{formatDuration(elapsedTime)}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">•</span>
              <span className="text-xs text-gray-600 dark:text-gray-400">{completedSteps}/{stepsCount} steps</span>
              {currentStep && status === 'running' && (
                <>
                  <span className="text-xs text-gray-500 dark:text-gray-400">•</span>
                  <span className="text-xs text-gray-600 dark:text-gray-400 truncate max-w-[150px]">{currentStep}</span>
                </>
              )}
            </div>

            {error && (
              <div className="mt-2 text-sm text-red-600 dark:text-red-400">
                <span className="font-medium">Error:</span> {error}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Progress bar */}
          <div className="w-32 hidden sm:block">
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${status === 'running' ? 'bg-blue-500' : status === 'success' ? 'bg-green-500' : status === 'error' ? 'bg-red-500' : 'bg-yellow-500'}`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400 w-8 text-right">{progressPercent}%</span>

          {status === 'running' && (
            <button
              onClick={onStop}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/50 hover:bg-red-200 dark:hover:bg-red-900 rounded-md transition-colors"
            >
              <StopIcon className="w-4 h-4" />
              Stop
            </button>
          )}
          {status !== 'running' && (
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
            >
              <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// View mode tabs
const ViewTabs = ({ activeView, onViewChange, versionsCount = 0, logsCount = 0, secretsCount = 0 }) => {
  const baseClasses = 'px-4 py-2 text-sm font-medium rounded-md transition-colors';
  const activeClasses = 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm';
  const inactiveClasses = 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white';

  const tabs = [
    { id: 'tree', label: 'Tree View' },
    { id: 'steps', label: 'Steps View' },
    { id: 'versions', label: 'Versioning', count: versionsCount },
    { id: 'logs', label: 'Run Logs', count: logsCount },
    { id: 'secrets', label: 'Secrets', count: secretsCount },
  ];

  return (
    <div className="inline-flex items-stretch p-1 bg-gray-100 dark:bg-gray-700 rounded-lg">
      {tabs.map((tab, index) => (
        <div key={tab.id} className="flex items-center">
          {index > 0 && (
            <div className="w-px h-5 bg-gray-300 dark:bg-gray-500 mx-0.5" />
          )}
          <button
            onClick={() => onViewChange(tab.id)}
            className={`${baseClasses} ${activeView === tab.id ? activeClasses : inactiveClasses}`}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className={`ml-1.5 px-1.5 py-0.5 text-xs rounded-full ${activeView === tab.id
                ? 'bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300'
                : 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
                }`}>
                {tab.count}
              </span>
            )}
          </button>
        </div>
      ))}
    </div>
  );
};

const PlaybookDetail = () => {
  const { id } = useParams();
  const playbook = getPlaybookById(id);
  const [viewMode, setViewMode] = useState('tree');
  const [selectedNode, setSelectedNode] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    frequency: 'on_event',
    changes: ''
  });
  const [testRun, setTestRun] = useState(null);
  const testRunTimerRef = useRef(null);
  const [isEnabled, setIsEnabled] = useState(playbook?.meta?.enabled ?? playbook?.active ?? true);

  // Secrets state - initialize with playbook secrets or demo data
  const [secrets, setSecrets] = useState(() => {
    if (playbook?.secrets) return playbook.secrets;
    // Demo secrets for the playbook
    return [
      {
        id: 'secret-1',
        key: 'XERO_CLIENT_ID',
        value: 'xero_abc123def456',
        description: 'Xero OAuth2 Client ID',
        createdAt: '2025-01-10T10:00:00Z',
        updatedAt: '2025-01-10T10:00:00Z',
        usedIn: ['xero-upsert-contact', 'xero-create-invoice']
      },
      {
        id: 'secret-2',
        key: 'XERO_CLIENT_SECRET',
        value: 'secret_xyz789ghi012',
        description: 'Xero OAuth2 Client Secret',
        createdAt: '2025-01-10T10:00:00Z',
        updatedAt: '2025-01-12T15:30:00Z',
        usedIn: ['xero-upsert-contact', 'xero-create-invoice']
      },
      {
        id: 'secret-3',
        key: 'FELLOH_API_KEY',
        value: 'fk_live_abcdefghijklmnop',
        description: 'Felloh API key for authentication',
        createdAt: '2025-01-05T09:00:00Z',
        updatedAt: '2025-01-05T09:00:00Z',
        usedIn: ['felloh-auth']
      }
    ];
  });

  // Secrets handlers
  const handleAddSecret = (newSecret) => {
    setSecrets(prev => [...prev, newSecret]);
  };

  const handleEditSecret = (updatedSecret) => {
    setSecrets(prev => prev.map(s => s.id === updatedSecret.id ? { ...s, ...updatedSecret } : s));
  };

  const handleDeleteSecret = (secretId) => {
    if (window.confirm('Are you sure you want to delete this secret? This action cannot be undone.')) {
      setSecrets(prev => prev.filter(s => s.id !== secretId));
    }
  };

  // Auto-select first node when playbook loads
  useEffect(() => {
    if (playbook?.nodes?.length > 0 && !selectedNode) {
      // Find entry node (no incoming connections) or use first node
      const incomingConnections = {};
      Object.values(playbook.connections || {}).forEach(connData => {
        connData?.main?.forEach(outputs => {
          outputs?.forEach(conn => {
            incomingConnections[conn.node] = true;
          });
        });
      });
      const entryNode = playbook.nodes.find(n => !incomingConnections[n.name] && !n.disabled) || playbook.nodes[0];
      if (entryNode) {
        setSelectedNode({ ...entryNode, integration: getIntegration(entryNode.type) });
      }
    }
  }, [playbook]);

  const handleNodeSelect = (node) => {
    setSelectedNode(selectedNode?.id === node.id ? null : node);
  };

  const toggleEditModal = () => {
    if (!editModalOpen && playbook) {
      // Initialize form with current playbook data
      const schedule = getPlaybookSchedule(playbook);
      setEditFormData({
        name: playbook.name,
        frequency: schedule === 'on_event' ? 'on_event' : schedule,
        changes: ''
      });
    }
    setEditModalOpen(!editModalOpen);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    // TODO: Implement playbook update API call
    console.log('Updating playbook:', editFormData);
    toggleEditModal();
  };

  // Test run handlers
  const startTestRun = useCallback(() => {
    const steps = playbook?.nodes || [];
    const stepNames = steps.map(s => s.name || s.type);

    setTestRun({
      status: 'running',
      startTime: Date.now(),
      currentStep: stepNames[0] || 'Initializing',
      completedSteps: 0,
      error: null
    });

    // Simulate step execution
    let currentStepIndex = 0;
    const totalSteps = steps.length || 3;

    const simulateSteps = () => {
      testRunTimerRef.current = setTimeout(() => {
        currentStepIndex++;

        if (currentStepIndex >= totalSteps) {
          // Randomly succeed or fail for demo
          const success = Math.random() > 0.2;
          setTestRun(prev => ({
            ...prev,
            status: success ? 'success' : 'error',
            completedSteps: totalSteps,
            currentStep: null,
            duration: Date.now() - prev.startTime,
            error: success ? null : 'Failed to execute step: API timeout'
          }));
        } else {
          setTestRun(prev => ({
            ...prev,
            completedSteps: currentStepIndex,
            currentStep: stepNames[currentStepIndex] || `Step ${currentStepIndex + 1}`
          }));
          simulateSteps();
        }
      }, 800 + Math.random() * 1200);
    };

    simulateSteps();
  }, [playbook]);

  const stopTestRun = useCallback(() => {
    if (testRunTimerRef.current) {
      clearTimeout(testRunTimerRef.current);
    }
    setTestRun(prev => prev ? {
      ...prev,
      status: 'stopped',
      duration: Date.now() - prev.startTime
    } : null);
  }, []);

  const closeTestRun = useCallback(() => {
    setTestRun(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (testRunTimerRef.current) {
        clearTimeout(testRunTimerRef.current);
      }
    };
  }, []);

  if (!playbook) {
    return (
      <div className="text-center py-12">
        <CommandLineIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Playbook not found</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          The playbook you&apos;re looking for doesn&apos;t exist.
        </p>
        <div className="mt-6">
          <Link
            to="/playbooks"
            className="inline-flex items-center gap-2 px-4 py-2 bg-violet-800 hover:bg-violet-600 text-white rounded-md text-sm font-medium transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Playbooks
          </Link>
        </div>
      </div>
    );
  }

  // Derive data from n8n format
  const description = getPlaybookDescription(playbook);
  const schedule = getPlaybookSchedule(playbook);
  const stepsDetail = getStepsDetail(playbook);
  const stepsCount = playbook.nodes?.length || 0;
  const runs = playbook.meta?.runs || 0;
  const lastRun = playbook.meta?.lastRun;

  const toggleEnabled = () => {
    setIsEnabled(prev => !prev);
    // TODO: Implement API call to update playbook status
  };

  return (
    <React.Fragment>
      <Head title={playbook ? playbook.name : 'Playbook'} />
    <div className="pb-12">
      {/* Breadcrumb */}
      <div className="mb-6">
        <Link
          to="/playbooks"
          className="inline-flex items-center gap-2 text-sm text-violet-800 hover:text-violet-600 dark:text-violet-400 dark:hover:text-violet-300 transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Playbooks
        </Link>
      </div>

      {/* Test Run Status Box */}
      <TestRunStatusBox
        testRun={testRun}
        onClose={closeTestRun}
        onStop={stopTestRun}
        stepsCount={playbook.nodes?.length || 0}
      />

      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 mb-6 clearfix h-auto">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {playbook.name}
              </h1>
              {isEnabled ? (
                <span className="inline-flex clearfix items-center gap-1 px-3 py-2 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                  <PlayIcon className="w-4 h-4" />
                  Enabled
                </span>
              ) : (
                <span className="inline-flex clearfix items-center gap-1 px-3 py-2 rounded-full text-sm font-medium bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300">
                  <PauseIcon className="w-4 h-4" />
                  Disabled
                </span>
              )}
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {playbook.versionId}
              </span>
            </div>
            <p className="text-gray-600 dark:text-gray-400">
              {description}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleEnabled}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${isEnabled
                ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-900 dark:text-yellow-300 dark:hover:bg-yellow-800'
                : 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-300 dark:hover:bg-green-800'
                }`}
            >
              {isEnabled ? (
                <>
                  <PauseIcon className="w-4 h-4" />
                  Disable
                </>
              ) : (
                <>
                  <PlayIcon className="w-4 h-4" />
                  Enable
                </>
              )}
            </button>
            <button
              onClick={startTestRun}
              disabled={testRun?.status === 'running'}
              className={`inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-md text-sm font-medium transition-colors ${
                testRun?.status === 'running'
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-600'
              }`}
            >
              <PlayIcon className="w-4 h-4" />
              Test Run
            </button>
            <button
              onClick={toggleEditModal}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              <PencilIcon className="w-4 h-4" />
              Edit
            </button>
          </div>
        </div>

        <div className="clearfix h-auto relative pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 dark:border-gray-700 clearfix h-auto">
            <div className="flex items-center gap-3 clearfix h-auto">
              <CommandLineIcon className="w-5 h-5 text-gray-400" />
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Steps</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">{stepsCount}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 clearfix h-auto">
              <ClockIcon className="w-5 h-5 text-gray-400" />
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Schedule</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">{getScheduleLabel(schedule)}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 clearfix h-auto">
              <ClockIcon className="w-5 h-5 text-gray-400" />
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Total Runs</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">{runs}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 clearfix h-auto">
              <ClockIcon className="w-5 h-5 text-gray-400" />
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Last Run</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {lastRun ? moment(lastRun).format('MMM D, YYYY [at] HH:mm') : 'Never'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4 clearfix h-auto mb-4">
        <ViewTabs
          activeView={viewMode}
          onViewChange={setViewMode}
          versionsCount={Math.max((playbook.versions || []).length, 1)}
          logsCount={(playbook.logs || []).length}
          secretsCount={secrets.length}
        />
      </div>

      {/* Workflow Section */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        {viewMode === 'tree' && (
          <div className="flex items-center justify-between mb-6 flex-wrap">
            {/* Legend - only show for tree view */}

            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full border-2 border-green-500 bg-white" />
                <span className="text-gray-600 dark:text-gray-400">Success</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full border-2 border-red-400 bg-white" />
                <span className="text-gray-600 dark:text-gray-400">Error</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full border-2 border-violet-400 bg-white" />
                <span className="text-gray-600 dark:text-gray-400">Path</span>
              </div>
            </div>

          </div>
        )}

        <div className="flex gap-6">
          <div className="flex-1 overflow-x-auto pb-4">
            {viewMode === 'tree' && (
              playbook?.nodes?.length > 0 ? (
                <WorkflowTree
                  n8nNodes={playbook.nodes}
                  connections={playbook.connections}
                  onNodeSelect={handleNodeSelect}
                  selectedNodeId={selectedNode?.id}
                />
              ) : (
                <LegacyStepsList steps={stepsDetail} />
              )
            )}

            {viewMode === 'steps' && (
              <StepsView n8nNodes={playbook?.nodes} connections={playbook?.connections} />
            )}

            {viewMode === 'versions' && (
              <div className="space-y-3">
                {(playbook.versions && playbook.versions.length > 0) ? (
                  [...playbook.versions]
                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                    .map((version) => (
                      <div
                        key={version.id}
                        className={`p-4 rounded-lg border ${version.isCurrent
                          ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                          : 'border-gray-200 dark:border-gray-700'
                          }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-base font-semibold text-gray-900 dark:text-white">
                              v{version.version}
                            </span>
                            {version.isCurrent && (
                              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-300">
                                Current
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {moment(version.createdAt).format('MMM D, YYYY [at] HH:mm')}
                            </span>
                            {!version.isCurrent && (
                              <button
                                onClick={() => {
                                  // TODO: Implement revert API call
                                  console.log('Reverting to version:', version.version);
                                }}
                                className="inline-flex h-auto items-center gap-1 px-3 py-1 text-sm font-medium text-violet-700 dark:text-violet-300 bg-violet-100 dark:bg-violet-900/50 hover:bg-violet-200 dark:hover:bg-violet-900 rounded-md transition-colors"
                              >
                                <ArrowPathIcon className="w-4 h-4" />
                                Revert
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                          {version.changeDescription}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500">
                          by {version.createdBy}
                        </p>
                      </div>
                    ))
                ) : (
                  <div className="p-4 rounded-lg border border-violet-500 bg-violet-50 dark:bg-violet-900/20">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-base font-semibold text-gray-900 dark:text-white">
                          v1
                        </span>
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-300">
                          Current
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                      Initial version
                    </p>
                  </div>
                )}
              </div>
            )}

            {viewMode === 'logs' && (
              <div className="space-y-2">
                {(playbook.logs && playbook.logs.length > 0) ? (
                  [...playbook.logs]
                    .sort((a, b) => new Date(b.startedAt) - new Date(a.startedAt))
                    .map((log) => (
                      <div
                        key={log.id}
                        className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            {log.status === 'success' && (
                              <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
                            )}
                            {log.status === 'error' && (
                              <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                            )}
                            {log.status === 'warning' && (
                              <span className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                            )}
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {log.triggerData?.booking_reference || log.executionId}
                            </span>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${log.status === 'success'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                              : log.status === 'error'
                                ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                              }`}>
                              {log.status}
                            </span>
                          </div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {moment(log.startedAt).format('MMM D, YYYY [at] HH:mm:ss')}
                          </span>
                        </div>
                        <div className="flex items-center gap-6 text-sm text-gray-500 dark:text-gray-400">
                          <span>{log.stepsExecuted} steps executed</span>
                          <span>{log.duration}ms duration</span>
                          <span>v{log.version}</span>
                          {log.triggerData?.amount && (
                            <span>£{log.triggerData.amount.toLocaleString()}</span>
                          )}
                        </div>
                        {log.error && (
                          <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded text-sm text-red-600 dark:text-red-400">
                            <span className="font-medium">{log.error.step}:</span> {log.error.message}
                          </div>
                        )}
                        {log.warning && (
                          <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded text-sm text-yellow-600 dark:text-yellow-400">
                            <span className="font-medium">{log.warning.step}:</span> {log.warning.message}
                          </div>
                        )}
                      </div>
                    ))
                ) : (
                  <div className="text-center py-12 bg-gray-50 dark:bg-gray-900 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
                    <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No run logs yet</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Run logs will appear here once the playbook has been executed.
                    </p>
                    <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
                      Use the &quot;Test Run&quot; button above to trigger a test execution.
                    </p>
                  </div>
                )}
              </div>
            )}

            {viewMode === 'secrets' && (
              <SecretsView
                secrets={secrets}
                onAddSecret={handleAddSecret}
                onEditSecret={handleEditSecret}
                onDeleteSecret={handleDeleteSecret}
              />
            )}
          </div>

          {/* Detail panel */}
          {viewMode === 'tree' && selectedNode && (
            <div className="flex-shrink-0">
              <StepDetailPanel
                node={selectedNode}
                onClose={() => setSelectedNode(null)}
                onNavigateToNode={handleNodeSelect}
              />
            </div>
          )}
        </div>
      </div>

      {/* Edit Playbook Modal */}
      <Modal isOpen={editModalOpen} toggle={toggleEditModal} size="lg">
        <ModalHeader
          toggle={toggleEditModal}
          className="bg-violet-800 text-xl text-white pt-4 pb-3"
          close={
            <button className="-mt-1 text-white" onClick={toggleEditModal}>
              <XMarkIcon className="h-6 w-6" />
            </button>
          }
        >
          <span className="flex items-center gap-2">
            <PencilIcon className="h-5 w-5" />
            Edit Playbook
          </span>
        </ModalHeader>

        <form onSubmit={handleEditSubmit}>
          <ModalBody className="p-6">
            <div className="space-y-6">
              {/* Name Field */}
              <div>
                <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Playbook Name
                </label>
                <input
                  type="text"
                  id="edit-name"
                  name="name"
                  value={editFormData.name}
                  onChange={handleEditInputChange}
                  placeholder="e.g., Payment Confirmation Flow"
                  className="block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                  required
                />
              </div>

              {/* Frequency Field */}
              <div>
                <label htmlFor="edit-frequency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  How often should this run?
                </label>
                <select
                  id="edit-frequency"
                  name="frequency"
                  value={editFormData.frequency}
                  onChange={handleEditInputChange}
                  className="block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                >
                  {FREQUENCY_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Changes Description Field - LLM Style */}
              <div>
                <label htmlFor="edit-changes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  What changes would you like to make?
                </label>
                <div className="relative">
                  <textarea
                    id="edit-changes"
                    name="changes"
                    value={editFormData.changes}
                    onChange={handleEditInputChange}
                    placeholder="Describe the changes you want to make in natural language. For example: 'Add an additional check to verify the customer email before sending the confirmation. Also add a Slack notification when a payment fails.'"
                    rows={6}
                    className="block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 resize-none"
                  />
                  <div className="absolute bottom-3 right-3 flex items-center gap-1 text-xs text-gray-400">
                    <SparklesIcon className="h-4 w-4" />
                    AI-powered
                  </div>
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Our AI will analyse your changes and update the playbook steps automatically.
                </p>
              </div>
            </div>
          </ModalBody>

          <ModalFooter className="bg-light">
            <button
              type="button"
              onClick={toggleEditModal}
              className="btn btn-outline-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn bg-violet-700 hover:bg-violet-800 text-white"
            >
              <SparklesIcon className="w-4 h-4 inline mr-1 -mt-0.5" />
              Update Playbook
            </button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
    </React.Fragment>
  );
};

export default PlaybookDetail;
